// @ts-strict-ignore
import {Component} from 'panel';
import produce from 'immer';

import {IronContextRegistry} from 'common/report/context-registry';
import {currentProjectStore, ProjectDetails} from 'common/report/stores/project-details';
import {djangoFetchJson} from 'common/report/queries/utils';
import {updateOrgDefaultAndProjectClusterId} from 'common/report/queries/app-api/legacy/orgs-settings-client';
import {apiClient} from 'common/util/api-client';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {withStylesPanel} from 'common/util/panel';
import {panel2react} from 'common/util/react';
import {FeatureGate, ProjectFeatureFlag} from 'common/report/constants';
import {getResidencyClusterId} from 'common/report/util';
import {ResidencyClusterCountryCode} from 'common/report/types';
import {Organization} from 'common/report/queries/app-api/user-metadata-client';

import {template} from './template';
import css from './index.module.styl?inline';

type CreateProjectFieldState = {
  timezones: any;
  selectedDataResidency: ResidencyClusterCountryCode;
  selectedOrganizationId: number;
  projectName: string;
  selectedTimezone: string;
  createProjectLoading: boolean;
  isB2BProject: boolean;
};
export class CreateProjectField extends Component<CreateProjectFieldState, any, any, any, IronContextRegistry> {
  static get attrsSchema(): any {
    return {
      ...super.attrsSchema,
      'organization-id': {type: `number`, description: `id for current organization`},
      'project-id': {type: `number`},
      orgs: {type: `json`},
      'is-setup': {type: `boolean`, description: `from setup page or not`},
      'is-fyt': {type: `boolean`, description: `from fyt page or not`},
      'button-area-align': {type: `string`},
      'fyt-org-settings': {type: `json`},
    };
  }
  get config() {
    return {
      template,
      useShadowDom: true,
      contexts: [`currentProject` as const, `projects` as const, `i18n` as const],
      defaultState: {
        timezones: [],
        selectedDataResidency: `US` as ResidencyClusterCountryCode,
        selectedOrganizationId: null,
        projectName: ``,
        selectedTimezone: null,
        createProjectLoading: false,
        isB2BProject: false,
      },
      helpers: {
        getTimezones: async () => {
          const timezonesUrl = `/api/app/timezones`;
          const response = await apiClient.get(timezonesUrl).then((res) => res);

          if (response) {
            const usPacific = (response as any[]).find(([_, name]) => name === `US/Pacific`);
            this.update({timezones: response, selectedTimezone: usPacific ? usPacific : response[0]});
          }
        },
        hasFeatureFlag: () =>
          this.getContext(`currentProject`)?.get().value?.hasFeatureFlag(ProjectFeatureFlag.B2BProject),
        hasIndiaDatacenterAccess: () =>
          this.getContext(`currentProject`).get()?.value?.getFeatureGate(FeatureGate.IndiaDatacenterAccess),
        createProject: async () => {
          this.update({createProjectLoading: true});

          if (this.attr(`is-fyt`)) {
            // from FYT flow, org has not been created yet
            const fytOrgSettings = this.attr(`fyt-org-settings`);
            const createOrgUrl = `/api/app/organizations/create_org`;
            try {
              const response = await djangoFetchJson(createOrgUrl, {
                org_name: fytOrgSettings.name,
                org_discoverability: fytOrgSettings.discoverability,
              });

              if (response.status === `ok`) {
                const orgId = response?.results?.org_id;
                this.update({selectedOrganizationId: orgId});
              }
            } catch (err) {
              this.dispatchEvent(new CustomEvent(`create`, {detail: {skipCreate: true, error: err.message}}));
              this.update({createProjectLoading: false});
              return; // return immediately since create org failed
            }
          }

          const {selectedDataResidency, projectName, selectedTimezone, selectedOrganizationId, isB2BProject} =
            this.state;
          const createProjectUrl = `/api/app/organizations/${selectedOrganizationId}/create-project`;
          const updateProjectUrl = `/projects/update/${this.attr(`project-id`)}`;

          const clusterId = getResidencyClusterId(selectedDataResidency);

          const projectId = this.attr(`project-id`);
          try {
            let response;
            if (this.attr(`is-setup`)) {
              // if coming straight from setup page, we will just edit their existing project that was
              // automatically created
              response = await updateOrgDefaultAndProjectClusterId(selectedOrganizationId, projectId, clusterId)
                .then(() =>
                  djangoFetchJson(updateProjectUrl, {
                    name: projectName,
                    timezone: selectedTimezone[0],
                    is_b2b: isB2BProject,
                  }),
                )
                .catch((err) => {
                  throw err;
                });
            } else {
              response = await apiClient
                .post(createProjectUrl, {
                  cluster_id: clusterId,
                  project_name: projectName,
                  timezone_id: selectedTimezone[0],
                  is_b2b: isB2BProject,
                })
                .then((res) => res);
            }

            if (response) {
              if (this.attr(`is-fyt`)) {
                this.dispatchEvent(
                  new CustomEvent(`create`, {detail: {response, projectName, orgId: selectedOrganizationId}}),
                );
              } else {
                // update the project resource immediately if not FYT since it needs to update on the page
                const currentProject = this.getContext(`currentProject`);
                if (currentProject) {
                  currentProject.update({name: projectName});
                  currentProjectStore.update(
                    produce(currentProjectStore.value, (project: ProjectDetails) => {
                      project.name = projectName;
                    }),
                  );
                }
                this.helpers.clickedCancel();
                this.dispatchEvent(new CustomEvent(`create`, {detail: {skipCreate: true, response, projectName}}));
              }
            }
          } catch (err) {
            this.dispatchEvent(new CustomEvent(`create`, {detail: {skipCreate: true, error: err.message}}));
          } finally {
            this.update({createProjectLoading: false});
          }
        },
        clickedCancel: () => {
          this.dispatchEvent(new CustomEvent(`cancel`));
        },
        orgHasPerm: (org: Organization, perm: string) => {
          if (!org || !perm) {
            return false;
          }
          return Array.isArray(org.permissions) && org.permissions.includes(perm);
        },
        handleChangeSelectedTimezone: (ev) => {
          const {timezones} = this.state;
          this.update({
            selectedTimezone: timezones.find((timezoneEntry) => timezoneEntry[1] === ev.detail.value),
          });
        },
        isSubmitDisabled: () => {
          const {projectName, selectedTimezone, createProjectLoading} = this.state;
          return !projectName || !projectName.trim() || !selectedTimezone || createProjectLoading;
        },
      },
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.update({selectedOrganizationId: this.attr(`organization-id`)});
    if (!this.state.timezones.length) {
      this.helpers.getTimezones();
    }
  }
}

export const CreateProjectFieldEl = defineAndMakeJsxFactory(
  `mp-create-project-field`,
  withStylesPanel(CreateProjectField, css),
);
export const CreateProjectFieldReact = panel2react(`mp-create-project-field`, CreateProjectField);
